// Salaiset kohteet, pelaajan ja ylläpitäjän puhdas logiikka (näkymä: HiddenAdmin.jsx, main.jsx).
//
// Pelaaja ei koskaan tiedä salaisen kohteen paikkaa ennen löytöä. Palvelin kertoo lähellä vain
// vihjeen ja epätarkan renkaan; tässä päätetään, milloin palvelimelta kysytään ja mitä ilmoitetaan.
// Ylläpitäjän lomake muunnetaan tässä rajapinnan muotoon samoilla rajoilla kuin palvelimella
// (backend/app/hidden.py), jotta virheet näkyvät ennen lähetystä.

import { distanceM } from "./geo.js";

/** Vähin väli kyselyiden välillä ja väli, jonka jälkeen kysytään uudelleen vaikkei liikuttaisi. */
export const POLL_MIN_MS = 15_000;
export const POLL_MAX_MS = 60_000;
/** Näin pitkän matkan liikkuminen riittää uuteen kyselyyn (vihjesäde on vähintään 50 m). */
export const POLL_MOVE_M = 50;

export const LIMITS = {
  name: 80,
  hint: 300,
  description: 2000,
  hintRadius: [50, 1000],
  visitRadius: [10, 100],
  points: [1, 1000],
};

const validPos = (p) => Boolean(p) && Number.isFinite(p.latitude) && Number.isFinite(p.longitude);

/**
 * Pitääkö vihjeet kysyä nyt? `last` on edellinen kysely {at, latitude, longitude} tai null.
 * Kyselyjä ei tehdä useammin kuin POLL_MIN_MS välein; sen jälkeen kysytään, kun on liikuttu
 * POLL_MOVE_M tai kun edellisestä on POLL_MAX_MS.
 */
export function pollDue(last, pos, now) {
  if (!validPos(pos)) return false;
  if (!last) return true;
  const elapsed = now - last.at;
  if (elapsed < POLL_MIN_MS) return false;
  if (elapsed >= POLL_MAX_MS) return true;
  return distanceM(last.latitude, last.longitude, pos.latitude, pos.longitude) >= POLL_MOVE_M;
}

/** Vihjeet, joista ei ole vielä ilmoitettu tässä istunnossa. `known` on Set tunnisteista; se päivittyy. */
export function unannounced(hints, known) {
  const fresh = [];
  for (const hint of hints ?? []) {
    if (!hint || typeof hint.id !== "string" || known.has(hint.id)) continue;
    known.add(hint.id);
    fresh.push(hint);
  }
  return fresh;
}

/** Ilmoitus uudesta vihjeestä. Ei koskaan nimeä tai paikkaa, vain vihje ja palkinto. */
export function hintToast(hint) {
  return { kind: "info", what: "Jotain erikoista lähellä", sub: hint.hint ? hint.hint : `Salainen paikka, ${hint.points} p löydöstä.` };
}

/** Kartalle piirrettävät renkaat: vain kelvolliset. */
export function ringsOf(hints) {
  return (hints ?? [])
    .map((h) => h?.ring)
    .filter((r) => r && Number.isFinite(r.lat) && Number.isFinite(r.lon) && Number.isFinite(r.radius_m) && r.radius_m > 0);
}

// --- Ylläpitäjän lomake ------------------------------------------------------------------

export function emptyForm(pos) {
  return {
    id: null,
    name: "",
    hint: "",
    description: "",
    lat: validPos(pos) ? pos.latitude.toFixed(6) : "",
    lon: validPos(pos) ? pos.longitude.toFixed(6) : "",
    hintRadius: "300",
    visitRadius: "30",
    points: "150",
    limited: false,
    validFrom: "",
    validTo: "",
    daily: false,
    dailyFrom: "20:00",
    dailyTo: "23:00",
    published: true,
  };
}

/** Palvelimen kohde lomakkeeksi. */
export function formFromPlace(place) {
  return {
    id: place.id,
    name: place.name ?? "",
    hint: place.hint ?? "",
    description: place.description ?? "",
    lat: String(place.lat),
    lon: String(place.lon),
    hintRadius: String(place.hint_radius_m),
    visitRadius: String(place.visit_radius_m),
    points: String(place.points),
    limited: Boolean(place.valid_from || place.valid_to),
    validFrom: place.valid_from ?? "",
    validTo: place.valid_to ?? "",
    daily: Boolean(place.daily_from && place.daily_to),
    dailyFrom: place.daily_from ?? "20:00",
    dailyTo: place.daily_to ?? "23:00",
    published: Boolean(place.published),
  };
}

function number(text) {
  const value = Number(String(text ?? "").trim().replace(",", "."));
  return String(text ?? "").trim() === "" ? NaN : value;
}

function inRange(value, [min, max]) {
  return Number.isFinite(value) && value >= min && value <= max;
}

/**
 * Lomake rajapinnan muotoon. Palauttaa {payload, errors}; virheitä ei ole, kun errors on tyhjä.
 * Virheteksti kertoo, mikä kenttä on pielessä.
 */
export function formToPayload(form) {
  const errors = [];
  const name = form.name.trim().replace(/\s+/g, " ");
  if (!name) errors.push("Anna kohteelle nimi.");
  if (name.length > LIMITS.name) errors.push(`Nimi saa olla enintään ${LIMITS.name} merkkiä.`);
  const hint = form.hint.trim().replace(/\s+/g, " ");
  if (hint.length > LIMITS.hint) errors.push(`Vihje saa olla enintään ${LIMITS.hint} merkkiä.`);
  const description = form.description.trim();
  if (description.length > LIMITS.description) errors.push(`Kuvaus saa olla enintään ${LIMITS.description} merkkiä.`);

  const lat = number(form.lat);
  const lon = number(form.lon);
  if (!inRange(lat, [-90, 90]) || !inRange(lon, [-180, 180])) errors.push("Sijainti puuttuu tai on virheellinen. Käytä nykyistä sijaintia tai valitse paikka kartalta.");

  const hintRadius = number(form.hintRadius);
  const visitRadius = number(form.visitRadius);
  if (!inRange(hintRadius, LIMITS.hintRadius)) errors.push(`Vihjesäteen pitää olla ${LIMITS.hintRadius[0]}–${LIMITS.hintRadius[1]} m.`);
  if (!inRange(visitRadius, LIMITS.visitRadius)) errors.push(`Käyntisäteen pitää olla ${LIMITS.visitRadius[0]}–${LIMITS.visitRadius[1]} m.`);
  if (inRange(hintRadius, LIMITS.hintRadius) && inRange(visitRadius, LIMITS.visitRadius) && visitRadius >= hintRadius) {
    errors.push("Vihjesäteen pitää olla suurempi kuin käyntisäteen.");
  }
  const points = number(form.points);
  if (!inRange(points, LIMITS.points) || !Number.isInteger(points)) errors.push(`Pisteiden pitää olla kokonaisluku ${LIMITS.points[0]}–${LIMITS.points[1]}.`);

  let validFrom = null;
  let validTo = null;
  if (form.limited) {
    validFrom = form.validFrom || null;
    validTo = form.validTo || null;
    if (!validFrom && !validTo) errors.push("Anna alku- tai loppupäivä, tai valitse että kohde on aina löydettävissä.");
    if (validFrom && validTo && validTo < validFrom) errors.push("Loppupäivä ei voi olla ennen alkupäivää.");
  }
  let dailyFrom = null;
  let dailyTo = null;
  if (form.daily) {
    dailyFrom = form.dailyFrom || null;
    dailyTo = form.dailyTo || null;
    if (!dailyFrom || !dailyTo) errors.push("Anna kellonajan alku ja loppu.");
    else if (dailyFrom === dailyTo) errors.push("Kellonajan alku ja loppu eivät voi olla samat.");
  }

  const payload = {
    name,
    hint,
    description,
    lat,
    lon,
    hint_radius_m: hintRadius,
    visit_radius_m: visitRadius,
    points,
    valid_from: validFrom,
    valid_to: validTo,
    daily_from: dailyFrom,
    daily_to: dailyTo,
    published: Boolean(form.published),
  };
  return { payload, errors };
}

const dayText = (iso) => `${Number(iso.slice(8))}.${Number(iso.slice(5, 7))}.`;

/** Aikaikkuna luettavaksi. Tyhjä merkkijono = aina löydettävissä. */
export function windowText(place) {
  const parts = [];
  if (place.valid_from || place.valid_to) parts.push(`${place.valid_from ? dayText(place.valid_from) : ""}–${place.valid_to ? dayText(place.valid_to) : ""}`);
  if (place.daily_from && place.daily_to) parts.push(`klo ${place.daily_from}–${place.daily_to}`);
  return parts.join(", ");
}

/** Ylläpitäjän listan tilarivi: julkaistu vai luonnos, pisteet ja aikaikkuna. */
export function adminLine(place) {
  const window = windowText(place);
  return `${place.published ? "Julkaistu" : "Luonnos"}, ${place.points} p${window ? `, ${window}` : ", aina löydettävissä"}`;
}
